import StackTrace from 'stacktrace-js'
import {
  useCabinStore,
  useCruiseListStore,
  useDetailsStore,
  useFlexFlightStore,
  useFlightStore,
  useRailStore,
  useUserStore,
} from '../store'

/* eslint-disable no-console */

const STAGE = process.env.STAGE
const settings: any = {
  dev: {
    url: 'https://1ndq9e6s0b.execute-api.eu-central-1.amazonaws.com/test/log',
    key: 'wWk2bv0lAcaU2mEOanyu57IyeWhsTH1f3QDqNmZy',
  },
  test: {
    url: 'https://1ndq9e6s0b.execute-api.eu-central-1.amazonaws.com/test/log',
    key: 'wWk2bv0lAcaU2mEOanyu57IyeWhsTH1f3QDqNmZy',
  },
  prod: {
    url: 'https://t3y0c2s7y8.execute-api.eu-central-1.amazonaws.com/prod/log',
    key: 'QUpcGtb8wQ2mRWRjcwJZFxcGimzqDt54479A5z98',
  },
}

export const handleError = async ({ error, origin }: { error: any; origin?: string }) => {
  const msg = error?.message

  if (!msg) {
    error = new Error('no error message available')
  }

  const ignoredMessages = [
    "Cannot read property 'getClientRects' of undefined",
    "Unable to get property 'getClientRects' of undefined or null reference",
    'no error message available',
    'Network Error',
    'NetworkError when attempting to fetch resource.',
    'Importing a module script failed',
    'Load failed',
    'Unit Test error',
  ]

  if (ignoredMessages.includes(msg)) return

  let fileName = ''
  let functionName = ''
  let stack = ''

  if (!!error.stack) {
    stack = error.stack
    const frames = await StackTrace.fromError(error)

    if (!!frames?.length) {
      fileName = frames[0].fileName ?? ''
      functionName = frames[0].functionName ?? ''
    }
  }

  fileName = !!fileName ? fileName : !!error.fileName ? error.fileName : ''

  // not our problem, not our logging; otherwise it will spam our log
  if (fileName.includes('adobe')) return

  const cruiseListStore = useCruiseListStore()
  const detailsStore = useDetailsStore()
  const flexFlightStore = useFlexFlightStore()
  const flightStore = useFlightStore()
  const railStore = useRailStore()
  const userStore = useUserStore()
  const cabinStore = useCabinStore()

  const details = {
    origin: origin ?? '',
    cruise: cruiseListStore.selectedEntryId,
    details: {
      state: detailsStore.$state,
      agency: {
        agencyId: userStore.agencyId,
        agentId: userStore.agentId,
      },
      cabin: {
        type: cabinStore.type,
        category: cabinStore.category,
        cabin: cabinStore.cabin,
        selectedByUser: cabinStore.selectedByUser,
        userChoice: cabinStore.userChoice,
      },
      flexFlight: {
        selectedFlight: flexFlightStore.selectedFlight,
        selectedRailOption: flexFlightStore.selectedRailOption,
        selectedFilters: flexFlightStore.selectedFilters,
        railDirection: flexFlightStore.railDirection,
        birthdates: flexFlightStore.birthdates,
      },
      flight: {
        selectedFlight: flightStore.selectedFlight,
        selectedRailOption: flightStore.selectedRailOption,
        railDirection: flightStore.railDirectionRNF,
        railAdults: flightStore.railAdults,
      },
      rail: {
        selectedRailOption: railStore.selectedRailOption,
        railDirection: flightStore.railDirectionRNF,
        railAdults: flightStore.railAdults,
      },
    },
  }

  const log = {
    logLevel: 'ERROR',
    application: 'expinet',
    browser: navigator.userAgent,
    functionName,
    stackTrace: stack,
    url: window.location.toString(),
    error,
    details,
    message: msg,
  }

  if (STAGE === 'dev') {
    console.error(log)
    return
  }

  try {
    await send(log, STAGE)
  } catch (error) {
    handleError({ error, origin: 'handleError - failed to log error' })
  }
}

export const handleWarning = async (msg: string, data?: object) => {
  const cruiseListStore = useCruiseListStore()

  const details = {
    ...data,
    cruise: cruiseListStore.selectedEntryId,
  }

  const log = {
    logLevel: 'WARN',
    application: 'expinet',
    browser: navigator.userAgent,
    url: window.location.toString(),
    message: msg,
    details,
    isInternalUser: isInternalUser(),
  }

  if (STAGE === 'dev') {
    console.warn(log)
    return
  }

  try {
    await send(log, STAGE)
  } catch (error) {
    handleError({ error, origin: 'handleWarning - failed to log warning' })
  }
}

export const handleInfo = async (msg: string, data: object) => {
  const cruiseListStore = useCruiseListStore()

  const details = {
    ...data,
    cruise: cruiseListStore.selectedEntryId,
  }

  const log = {
    application: 'expinet',
    logLevel: 'INFO',
    browser: navigator.userAgent,
    url: window.location.toString(),
    details,
    isInternalUser: isInternalUser(),
    message: msg,
  }

  if (STAGE === 'dev') {
    console.info(log)
    return
  }

  try {
    await send(log, STAGE)
  } catch (error) {
    handleError({ error, origin: 'handleInfo - failed to log info' })
  }
}

const send = async (log: any, STAGE: any) => {
  const params: RequestInit = {
    mode: 'cors',
    cache: 'no-cache',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': settings[STAGE].key,
    },
    body: JSON.stringify(log),
  }

  await fetch(settings[STAGE].url, params)
}

export const isInternalUser = () => {
  const userStore = useUserStore()
  const linkAgencyList = ['AIDA AKC', 'AIDASEE', '27681', '40272']
  const agencyNumber = userStore.agencyId

  return !!agencyNumber && linkAgencyList.includes(agencyNumber)
}

export const exportForTesting = {
  isInternalUser,
}
